import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="survey-response"
export default class extends Controller {
  static targets = ["question", "skipButton", "section"];
  static values = { allConditions: Array, disableHiddenInputs: Boolean };

  connect() {
    this.questionElements = new Map();
    this.questionConditions = new Map();
    this.loadQuestionConditions();
    this.setupEventListeners();
    this.updateSkipButtonVisibility();
  }

  loadQuestionConditions() {
    // Load display conditions from centralized data
    if (this.allConditionsValue && Array.isArray(this.allConditionsValue)) {
      this.allConditionsValue.forEach((questionData) => {
        const questionId = questionData.id;
        const conditions = questionData.conditions;

        if (
          conditions &&
          conditions.conditions &&
          conditions.conditions.length > 0
        ) {
          this.questionConditions.set(questionId, conditions);
        }
      });
    }

    // Map question elements by ID
    this.questionTargets.forEach((questionElement) => {
      const questionId = parseInt(questionElement.dataset.questionId);
      this.questionElements.set(questionId, questionElement);
    });

    // Initial evaluation of all conditional questions
    this.evaluateAllConditions();
  }

  setupEventListeners() {
    // Listen for changes on all form inputs
    this.element.addEventListener("change", (event) => {
      if (event.target.matches("input, select, textarea")) {
        this.handleResponseChange(event);
      }
    });

    // An upload swaps in its file row through a Turbo Stream, with no change
    // event. dropzone:success doesn't bubble, so listen while it travels down.
    this.element.addEventListener("dropzone:success", (event) => this.handleResponseChange(event), true);

    // Signing or clearing a signature announces itself with this event, not
    // an input event.
    this.element.addEventListener("signature-pad:change", (event) => this.handleResponseChange(event));

    // Also listen for input events for real-time text updates
    this.element.addEventListener("input", (event) => {
      if (
        event.target.matches(
          'input[type="text"], input[type="number"], input[type="email"], input[type="tel"], textarea',
        )
      ) {
        this.handleResponseChange(event);
      }
    });
  }

  handleResponseChange(event) {
    // Debounce rapid input changes
    clearTimeout(this.evaluationTimeout);
    this.evaluationTimeout = setTimeout(() => {
      this.evaluateAllConditions();
      this.updateSkipButtonVisibility();
    }, 100);
  }

  // Hiding a question clears its answer, which can change a rule checked
  // earlier in the same pass (one on a later question), so check again until
  // nothing moves. The cap stops rules that depend on each other in a loop.
  evaluateAllConditions() {
    for (let pass = 0; pass < 10; pass++) {
      const changed = this.hasSectionTarget ? this.evaluateSectionConditions() : this.evaluateQuestionConditions();
      if (!changed) break;
    }
  }

  evaluateQuestionConditions() {
    let changed = false;
    this.questionConditions.forEach((conditions, questionId) => {
      const shouldShow = this.evaluateConditions(conditions);
      changed = this.toggleQuestionVisibility(questionId, shouldShow) || changed;
    });
    return changed;
  }

  evaluateSectionConditions() {
    let changed = false;
    this.sectionTargets.forEach((section) => {
      const conditions = JSON.parse(section.dataset.displayConditions);
      const visible = !conditions.conditions?.length || this.evaluateConditions(conditions);
      changed = section.classList.contains("hidden") === visible || changed;
      section.classList.toggle("hidden", !visible);
      section.querySelectorAll('[data-survey-response-target="question"]').forEach((question) => {
        const id = parseInt(question.dataset.questionId);
        const rules = this.questionConditions.get(id);
        changed = this.toggleQuestionVisibility(id, visible && (!rules || this.evaluateConditions(rules))) || changed;
      });
    });
    return changed;
  }

  evaluateConditions(conditions) {
    const operator = conditions.operator || "AND";
    const conditionResults = conditions.conditions.map((condition) => {
      return this.evaluateSingleCondition(condition);
    });

    switch (operator.toUpperCase()) {
      case "AND":
        return conditionResults.every((result) => result);
      case "OR":
        return conditionResults.some((result) => result);
      default:
        return true;
    }
  }

  evaluateSingleCondition(condition) {
    const questionId = condition.question_id;
    const conditionType = condition.type;

    if (!questionId) return false;

    const questionElement = this.questionElements.get(questionId);
    // A question this page doesn't render (an earlier section): the server
    // sends the rule's result from the saved answers.
    if (!questionElement) return condition.resolved ?? false;

    switch (conditionType) {
      case "presence":
        return this.evaluatePresenceCondition(questionElement);
      case "blank":
        return !this.evaluatePresenceCondition(questionElement);
      case "value":
        return this.evaluateValueCondition(questionElement, condition);
      default:
        return false;
    }
  }

  evaluatePresenceCondition(questionElement) {
    const questionType = questionElement.dataset.questionType;

    switch (questionType) {
      case "checkbox":
      case "radio":
      case "boolean":
      case "rating_scale":
      case "select":
        // An untouched dropdown still has its empty placeholder selected.
        return (
          questionElement.querySelector("input:checked") !== null ||
          Array.from(questionElement.querySelectorAll("select")).some((select) => select.value !== "")
        );
      case "file_upload":
        if (this.disableHiddenInputsValue && questionElement.dataset.contextResponse === "present") return true;
        // A saved file renders as a file row, an upload too once its stream lands.
        if (questionElement.querySelector("[data-response-file]")) return true;
        const fileInput = questionElement.querySelector('input[type="file"]');
        return fileInput && fileInput.files.length > 0;
      case "signature":
        // A saved signature shows as a preview until it's cleared.
        if (questionElement.querySelector("[data-response-file]:not([hidden])")) return true;
        const sigInput = questionElement.querySelector('input[type="hidden"][data-signature-pad-target="input"]');
        return sigInput && sigInput.value.trim() !== "";
      case "instructions":
        return true; // Instructions don't require a response
      default:
        const textInputs = questionElement.querySelectorAll(
          'input[type="text"], input[type="number"], input[type="email"], input[type="tel"], textarea, input[type="date"], input[type="datetime-local"]',
        );
        return Array.from(textInputs).some(
          (input) => input.value.trim() !== "",
        );
    }
  }

  evaluateValueCondition(questionElement, condition) {
    const questionType = questionElement.dataset.questionType;
    const conditionOperator = condition.operator || "equals";
    const conditionValue = condition.value;

    if (!conditionValue) return false;

    switch (questionType) {
      case "checkbox":
      case "radio":
      case "select":
      case "likert_scale":
        return this.evaluateChoiceCondition(
          questionElement,
          conditionOperator,
          conditionValue,
        );
      case "number":
      case "rating_scale":
        return this.evaluateNumericCondition(
          questionElement,
          conditionOperator,
          conditionValue,
        );
      case "boolean":
        return this.evaluateBooleanCondition(
          questionElement,
          conditionOperator,
          conditionValue,
        );
      case "date":
      case "datetime":
        return this.evaluateDateCondition(
          questionElement,
          conditionOperator,
          conditionValue,
        );
      default:
        return this.evaluateTextCondition(
          questionElement,
          conditionOperator,
          conditionValue,
        );
    }
  }

  evaluateChoiceCondition(questionElement, operator, value) {
    const selectedValues = [];

    // Get selected values from checkboxes, radio buttons, or select elements
    const checkedInputs = questionElement.querySelectorAll("input:checked");
    checkedInputs.forEach((input) => {
      selectedValues.push(input.value);
    });

    const selectElements = questionElement.querySelectorAll("select");
    selectElements.forEach((select) => {
      if (select.value) {
        selectedValues.push(select.value);
      }
    });

    // Nothing picked matches no rule, "is not" and "none of" included, as on
    // the server.
    if (selectedValues.length === 0) return false;

    const wanted = [value].flat().map(String);

    switch (operator) {
      case "equals":
        return selectedValues.includes(value.toString());
      case "not_equals":
        return !selectedValues.includes(value.toString());
      case "any_of":
        return wanted.some((id) => selectedValues.includes(id));
      case "all_of":
        return wanted.every((id) => selectedValues.includes(id));
      case "none_of":
        return !wanted.some((id) => selectedValues.includes(id));
      default:
        return false;
    }
  }

  evaluateNumericCondition(questionElement, operator, value) {
    // Rating scales answer with a checked radio, numbers with a number input.
    const input = questionElement.querySelector('input[type="number"], input[type="radio"]:checked');
    if (!input || !input.value) return false;

    const numericResponse = parseFloat(input.value);
    if (isNaN(numericResponse)) return false;

    if (operator === "between") {
      const [low, high] = Array.isArray(value) ? value.map((v) => this.ruleNumber(v)) : [];
      return numericResponse >= low && numericResponse <= high;
    }

    const numericCondition = this.ruleNumber(value);
    if (isNaN(numericCondition)) return false;

    switch (operator) {
      case "at_least":
        return numericResponse >= numericCondition;
      case "at_most":
        return numericResponse <= numericCondition;
      case "equals":
        return numericResponse === numericCondition;
      case "not_equals":
        return numericResponse !== numericCondition;
      case "greater_than":
        return numericResponse > numericCondition;
      case "less_than":
        return numericResponse < numericCondition;
      default:
        return false;
    }
  }

  // A rule's number, read as strictly as the server's Float(): a blank, a
  // word or a list compares as nothing, where parseFloat reads "3abc" as 3.
  ruleNumber(value) {
    if (value === null || typeof value === "object") return NaN;
    const text = String(value).trim();
    return text === "" ? NaN : Number(text);
  }

  evaluateBooleanCondition(questionElement, operator, value) {
    const input = questionElement.querySelector(
      'input[type="radio"]:checked, select',
    );
    if (!input || input.value === "") return false;

    const responseValue = input.value;
    const responseBool = responseValue === "true" || responseValue === "1";
    const toBool = (v) => v === "true" || v === true || v === "1";
    const listed = [value].flat().map(toBool).includes(responseBool);
    if (operator === "any_of") return listed;
    if (operator === "none_of") return !listed;

    const conditionBool = toBool(value);

    switch (operator) {
      case "equals":
        return responseBool === conditionBool;
      case "not_equals":
        return responseBool !== conditionBool;
      default:
        return false;
    }
  }

  evaluateDateCondition(questionElement, operator, value) {
    const input = questionElement.querySelector(
      'input[type="date"], input[type="datetime-local"]',
    );
    if (!input || !input.value) return false;

    try {
      const responseDate = this.calendarDate(input.value);
      if (!responseDate) return false;

      if (operator === "more_than_ago" || operator === "within_last") {
        const cutoff = this.relativeDateCutoff(value);
        if (!cutoff) return false;
        return operator === "more_than_ago" ? responseDate < cutoff : responseDate >= cutoff;
      }

      const [first, last] = [value].flat();
      const conditionDate = this.calendarDate(first);
      if (!conditionDate) return false;

      switch (operator) {
        case "between": {
          const endDate = this.calendarDate(last);
          return Boolean(endDate) && responseDate >= conditionDate && responseDate <= endDate;
        }
        case "equals":
          return responseDate.getTime() === conditionDate.getTime();
        case "not_equals":
          return responseDate.getTime() !== conditionDate.getTime();
        case "greater_than":
          return responseDate > conditionDate;
        case "less_than":
          return responseDate < conditionDate;
        default:
          return false;
      }
    } catch (error) {
      return false;
    }
  }

  // The calendar day a date names, at UTC midnight, read the way the server's
  // Date.parse reads it: ISO first (a date-time's clock time dropped), then
  // day/month/year for slashes, so "10/07/2026" is 10 July on both sides.
  calendarDate(value) {
    if (value === null || typeof value === "object") return null;
    const text = String(value).trim();
    const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return new Date(Date.UTC(+iso[1], +iso[2] - 1, +iso[3]));
    const dayMonthYear = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (dayMonthYear) return new Date(Date.UTC(+dayMonthYear[3], +dayMonthYear[2] - 1, +dayMonthYear[1]));
    const parsed = new Date(text);
    if (isNaN(parsed.getTime())) return null;
    return new Date(Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()));
  }

  // The respondent's today, minus `{ amount, unit }`: the edge of "more than
  // 12 months ago". A month back from Mar 31 is Feb 28, as the server's
  // `Date.current - 1.month` has it. UTC midnight, to match how
  // `new Date("2026-10-01")` reads a date input.
  relativeDateCutoff({ amount, unit } = {}) {
    const today = new Date();
    const count = parseInt(amount, 10) || 0;
    const year = today.getFullYear();
    let month = today.getMonth();
    let day = today.getDate();
    switch (unit) {
      case "days":
        day -= count;
        break;
      case "weeks":
        day -= count * 7;
        break;
      case "months":
      case "years":
        month -= unit === "years" ? count * 12 : count;
        day = Math.min(day, new Date(Date.UTC(year, month + 1, 0)).getUTCDate());
        break;
      default:
        return null;
    }
    return new Date(Date.UTC(year, month, day));
  }

  evaluateTextCondition(questionElement, operator, value) {
    const input = questionElement.querySelector(
      'input[type="text"], input[type="email"], input[type="tel"], textarea',
    );
    // Blank or cleared matches no rule, as on the server.
    if (!input || !input.value.trim()) return false;

    const responseValue = input.value.trim();
    const conditionValue = value.toString().trim();

    switch (operator) {
      case "equals":
        return responseValue.toLowerCase() === conditionValue.toLowerCase();
      case "not_equals":
        return responseValue.toLowerCase() !== conditionValue.toLowerCase();
      case "contains":
        return responseValue
          .toLowerCase()
          .includes(conditionValue.toLowerCase());
      default:
        return false;
    }
  }

  scrollToNextRequired() {
    const next = this.findNextUnansweredRequired();
    if (next) {
      next.scrollIntoView({ behavior: "smooth", block: "center" });
      const badge = next.querySelector(".badge-soft-error");
      if (badge) {
        badge.classList.remove("animate-bounce");
        void badge.offsetWidth;
        badge.classList.add("animate-bounce");
        clearTimeout(this.badgeTimeout);
        this.badgeTimeout = setTimeout(() => badge.classList.remove("animate-bounce"), 3500);
      }
      const label = next.querySelector(".font-medium, .font-bold");
      if (label) {
        label.style.transition = "font-weight 0.3s ease";
        label.classList.remove("font-bold");
        label.classList.add("font-medium");
        void label.offsetWidth;
        label.classList.remove("font-medium");
        label.classList.add("font-bold");
        clearTimeout(this.labelTimeout);
        this.labelTimeout = setTimeout(() => {
          label.classList.remove("font-bold");
          label.classList.add("font-medium");
        }, 3500);
      }
    }
  }

  findNextUnansweredRequired() {
    for (const questionElement of this.questionTargets) {
      if (questionElement.classList.contains("hidden")) continue;
      if (questionElement.dataset.originallyRequired !== "true") continue;
      if (!this.isQuestionAnswered(questionElement)) return questionElement;
    }
    return null;
  }

  isQuestionAnswered(questionElement) {
    return this.evaluatePresenceCondition(questionElement);
  }

  updateSkipButtonVisibility() {
    if (!this.hasSkipButtonTarget) return;
    const next = this.findNextUnansweredRequired();
    this.skipButtonTarget.classList.toggle("hidden", !next);
  }

  // True when the question's visibility or answer changed.
  toggleQuestionVisibility(questionId, shouldShow) {
    const questionElement = this.questionElements.get(questionId);
    if (!questionElement) {
      return false;
    }
    const wasShown = !questionElement.classList.contains("hidden");

    if (this.disableHiddenInputsValue) {
      questionElement.querySelectorAll('[name^="input[answers]"]').forEach((input) => {
        input.disabled = !shouldShow;
      });
    }

    if (shouldShow) {
      questionElement.classList.remove("hidden");
      questionElement.style.display = "";
      // Re-enable form validation for visible questions
      this.setQuestionRequired(questionElement, true);
      return !wasShown;
    }

    questionElement.classList.add("hidden");
    questionElement.style.display = "none";
    // Disable form validation for hidden questions and clear their values
    this.setQuestionRequired(questionElement, false);
    const cleared = this.clearQuestionValues(questionElement);
    return wasShown || cleared;
  }

  setQuestionRequired(questionElement, required) {
    const inputs = questionElement.querySelectorAll("input, select, textarea");
    inputs.forEach((input) => {
      if (required && questionElement.dataset.originallyRequired === "true") {
        input.setAttribute("required", "required");
      } else {
        input.removeAttribute("required");
      }
    });
  }

  clearQuestionValues(questionElement) {
    // Signature answers live in hidden inputs, which the loop below skips.
    questionElement.querySelectorAll('[data-controller~="signature-pad"]').forEach((pad) => {
      const input = pad.querySelector('[data-signature-pad-target="input"]');
      if (!input?.value) return;

      const signaturePad = this.application.getControllerForElementAndIdentifier(pad, "signature-pad");
      if (signaturePad) signaturePad.clear();
      else input.value = "";
    });

    let cleared = false;

    // A saved upload is a row, not a filled file input, so delete it the way its
    // trash link does, without the confirm.
    questionElement.querySelectorAll("[data-response-file-remove]").forEach((link) => {
      fetch(link.href, {
        method: "DELETE",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
      });
      link.closest("[data-response-file]").remove();
      cleared = true;
    });

    const inputs = questionElement.querySelectorAll("input, select, textarea");
    inputs.forEach((input) => {
      // Skip hidden inputs to avoid clearing CSRF tokens and other important data
      if (input.type === "hidden") {
        return;
      }

      switch (input.type) {
        case "checkbox":
        case "radio":
          if (input.checked) {
            input.checked = false;
            cleared = true;
          }
          break;
        case "file":
          if (input.value) {
            input.value = "";
            cleared = true;
          }
          break;
        default:
          if (input.value) {
            input.value = "";
            cleared = true;
          }
      }
    });

    // Save the cleared answer so the server stops counting it either; a
    // question that held none needs no request.
    if (cleared) this.triggerAutoSave(questionElement);
    return cleared;
  }

  // A tick later: when the first check runs on connect, the question's own
  // auto-save controller (a descendant) hasn't connected yet. Sent at once,
  // not after the debounce, so a quick Next or Finish can't outrun it.
  triggerAutoSave(questionElement) {
    setTimeout(() => {
      // A respondent question's auto-save form sits inside it; elsewhere the
      // form can wrap the question.
      const autoSaveElement =
        questionElement.querySelector('[data-controller~="auto-save"]') ??
        questionElement.closest('[data-controller~="auto-save"]');
      if (!autoSaveElement) return;

      this.application.getControllerForElementAndIdentifier(autoSaveElement, "auto-save")?.flush();
    }, 0);
  }
}
